import { debounce } from 'lodash'
import { useEffect, useMemo, useRef } from 'react'

const DEFAULT_SCALE = 1

const renderPageToCanvas = async ({ pdfPage, canvasElement, scaleFactor }) => {
  try {
    const context = canvasElement.getContext('2d')
    const viewport = pdfPage.getViewport({ scale: scaleFactor ?? (window.SCALE || DEFAULT_SCALE) })

    const pixelRatio = window.devicePixelRatio || 1
    canvasElement.width = viewport.width * pixelRatio
    canvasElement.height = viewport.height * pixelRatio

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
      transform: [pixelRatio, 0, 0, pixelRatio, 0, 0],
    }

    const renderTask = pdfPage.render(renderContext)
    return renderTask
  } catch (error) {
    console.error('PDF Render Error:', error)
    throw error
  }
}

export function usePdfCanvasRenderer(pdfPage, scaleFactor) {
  const canvasRef = useRef(null)
  const activeRenderTask = useRef(null)

  const performRender = useMemo(
    () =>
      debounce(async (pageInstance, scaleFactor) => {
        const canvas = canvasRef.current
        if (!canvas || !pageInstance) return

        // Cancel any existing task before starting a new one
        if (activeRenderTask.current) {
          try {
            activeRenderTask.current.cancel()
          } catch (err) {
            // Task might already be finished
          }
        }

        try {
          const task = await renderPageToCanvas({
            pdfPage: pageInstance,
            canvasElement: canvas,
            scaleFactor,
          })

          activeRenderTask.current = task

          // Wait for the internal PDF.js promise to resolve
          await task.promise
        } catch (error) {
          console.error('Render Task Failed:', error)
        }
      }, 10),
    []
  )

  useEffect(() => {
    if (pdfPage) performRender(pdfPage, scaleFactor)

    return () => {
      if (activeRenderTask.current) activeRenderTask.current.cancel()
    }
  }, [pdfPage, scaleFactor, performRender])

  return canvasRef
}
